import FontAwesome from 'react-native-vector-icons/FontAwesome';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { createStackNavigator } from '@react-navigation/stack';
import { ActivityIndicator, Platform, StyleSheet, Text, View } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { ThemeProvider, useTheme } from './context/ThemeContext';
import { PlayerProvider } from './context/PlayerContext';
import { useAuth } from './context/AuthContext';
import { ChatStateProvider } from './context/ChatStateContext';
import { MissionsProvider } from './context/MissionsContext';
import { NotificationsProvider, useNotifications } from './context/NotificationsContext';
import { StoryProgressProvider } from './context/StoryProgressContext';
import { getThemeColors } from './utils/themeStyles';
import { useResponsive } from './hooks/useResponsive';
import DesktopLayout from './components/DesktopLayout';
import GlobalLoadingIndicator from './components/GlobalLoadingIndicator';

import MissionsScreen from './screens/MissionsScreen';
import ChatScreen from './screens/ChatScreen';
import NotificationsScreen from './screens/NotificationsScreen';
import ProfileScreen from './screens/ProfileScreen';
import CardDiscussionScreen from './screens/CardDiscussionScreen';
import CartScreen from './screens/CartScreen';
import ChapterDetailScreen from './screens/ChapterDetailScreen';
import ChaptersScreen from './screens/ChaptersScreen';
import MissionCardsScreen from './screens/MissionCardsScreen';
import MissionChatScreen from './screens/MissionChatScreen';
import OrderPipelineScreen from './screens/OrderPipelineScreen';
import QuizScreen from './screens/QuizScreen';
import SceneReaderScreen from './screens/SceneReaderScreen';
import TaskDetailScreen from './screens/TaskDetailScreen';
import TaskBoardScreen from './screens/TaskBoardScreen';
import MissionDetailScreen from './screens/MissionDetailScreen';
import UserStoriesScreen from './screens/UserStoriesScreen';
import UserStoryDetailScreen from './screens/UserStoryDetailScreen';
import LoginScreen from './screens/auth/LoginScreen';
import MyInformationScreen from './screens/MyInformationScreen';

const Tab = createBottomTabNavigator();
const Stack = createStackNavigator();
const AuthStack = createStackNavigator();
const PublicStack = createStackNavigator();

function TabNavigator() {
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const { unread } = useNotifications();

  return (
    <Tab.Navigator
      screenOptions={({ route }) => ({
        headerShown: false,
        tabBarActiveTintColor: colors.tabActive,
        tabBarInactiveTintColor: colors.tabInactive,
        tabBarStyle: {
          backgroundColor: colors.tabBackground,
          borderTopWidth: 1,
          borderTopColor: colors.border,
          paddingBottom: 10,
          paddingTop: 5,
          height: 70,
          elevation: 0,
          shadowOpacity: 0,
        },
        tabBarLabelStyle: {
          fontSize: 12,
          fontWeight: '600',
          marginTop: 2,
        },
        tabBarIcon: ({ focused, color, size }) => {
          if (route.name === 'Missions') {
            return (
              <View style={styles.tabIconContainer}>
                <FontAwesome name="bullseye" size={size} color={color} />
              </View>
            );
          }

          let iconName: string;
          let badgeCount: number | undefined;

          if (route.name === 'Chat') {
            iconName = focused ? 'chatbox' : 'chatbox-outline';
            badgeCount = 3;
          } else if (route.name === 'Notifications') {
            iconName = focused ? 'notifications' : 'notifications-outline';
            badgeCount = unread > 0 ? unread : undefined;
          } else {
            iconName = focused ? 'person' : 'person-outline';
          }

          return (
            <View style={styles.tabIconContainer}>
              <Ionicons name={iconName} size={size} color={color} />
              {badgeCount !== undefined && (
                <View style={[styles.badge, { backgroundColor: colors.badgeBackground }]}>
                  <Text style={[styles.badgeText, { color: colors.badgeText }]}>{badgeCount}</Text>
                </View>
              )}
            </View>
          );
        },
        tabBarItemStyle: {
          borderRadius: 10,
          marginHorizontal: 2,
          marginVertical: 2,
          paddingHorizontal: 12,
          paddingVertical: 8,
          minHeight: 50,
          paddingBottom: 10,
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
        },
        tabBarActiveBackgroundColor: colors.tabActiveBackground,
      })}
    >
      <Tab.Screen name="Missions" component={MissionsScreen} />
      <Tab.Screen name="Chat" component={ChatScreen} />
      <Tab.Screen name="Notifications" component={NotificationsScreen} />
      <Tab.Screen name="Account" component={ProfileScreen} />
    </Tab.Navigator>
  );
}

function MainNavigator() {
  const { isDesktop } = useResponsive();
  const MainComponent = isDesktop ? DesktopLayout : TabNavigator;

  return (
    <Stack.Navigator screenOptions={{ headerShown: false }}>
      <Stack.Screen name="Main" component={MainComponent} />
      <Stack.Screen name="CardDiscussion" component={CardDiscussionScreen} />
      <Stack.Screen name="Cart" component={CartScreen} options={{ presentation: "modal" }} />
      <Stack.Screen name="OrderPipeline" component={OrderPipelineScreen} options={{ presentation: "modal" }} />
      <Stack.Screen name="Chapters" component={ChaptersScreen} />
      <Stack.Screen name="ChapterDetail" component={ChapterDetailScreen} />
      <Stack.Screen name="MissionCards" component={MissionCardsScreen} />
      <Stack.Screen name="UserStories" component={UserStoriesScreen} />
      <Stack.Screen name="UserStoryDetail" component={UserStoryDetailScreen} />
      <Stack.Screen name="MissionChat" component={MissionChatScreen} />
      <Stack.Screen name="Quiz" component={QuizScreen} />
      <Stack.Screen name="SceneReader" component={SceneReaderScreen} />
      <Stack.Screen name="TaskDetail" component={TaskDetailScreen} />
      <Stack.Screen name="TaskBoard" component={TaskBoardScreen} />
      <Stack.Screen name="MissionDetail" component={MissionDetailScreen} />
      <Stack.Screen name="MyInformation" component={MyInformationScreen} />
    </Stack.Navigator>
  );
}

// Routes accessibles SANS authentification (le reste est protégé).
const PUBLIC_PATHS = ['/', '/cart', '/auth/callback', '/logout/callback', '/login'];

// Guard deep-link (web) : si un utilisateur déconnecté ouvre directement une URL
// protégée (ou si l'intercepteur 401 a mémorisé un returnTo), on démarre sur
// l'écran de connexion et on mémorise la page visée pour y revenir après login.
function computePublicInitialRoute(): 'PublicMissions' | 'Login' {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return 'PublicMissions';
  // Normalise (casse + slash final) : '/Login' -> '/login', '/cart/' -> '/cart'.
  const path = (window.location.pathname.toLowerCase().replace(/\/+$/, '') || '/');
  // On force le Login UNIQUEMENT si le chemin lui-même est protégé (deep link
  // ou page protégée après un 401 — l'URL est alors encore la page protégée).
  if (!PUBLIC_PATHS.includes(path)) {
    try {
      sessionStorage.setItem('ccjaw.returnTo', window.location.pathname + window.location.search);
    } catch {
      /* ignore */
    }
    return 'Login';
  }
  // Page publique : nettoyer un éventuel returnTo périmé (évite de rediriger
  // l'accueil vers Login à tort).
  try {
    sessionStorage.removeItem('ccjaw.returnTo');
  } catch {
    /* ignore */
  }
  return 'PublicMissions';
}

function PublicNavigator() {
  return (
    <PublicStack.Navigator initialRouteName={computePublicInitialRoute()} screenOptions={{ headerShown: false, cardStyle: { flex: 1 } }}>
      <PublicStack.Screen name="PublicMissions" component={MissionsScreen} />
      <PublicStack.Screen name="Login" component={LoginScreen} />
      <PublicStack.Screen name="Cart" component={CartScreen} options={{ presentation: 'modal' }} />
    </PublicStack.Navigator>
  );
}

function SplashScreen() {
  return (
    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
      <ActivityIndicator size="large" color="#4F46E5" />
    </View>
  );
}

export default function RootNavigator() {
  const { user, isLoading } = useAuth();

  return (
    <ThemeProvider>
      <PlayerProvider>
      <MissionsProvider>
      <NotificationsProvider>
      <StoryProgressProvider>
      <ChatStateProvider>
        <SafeAreaProvider>
          {isLoading
            ? <SplashScreen />
            : user
              ? <MainNavigator />
              : <PublicNavigator />
          }
          <GlobalLoadingIndicator />
        </SafeAreaProvider>
      </ChatStateProvider>
      </StoryProgressProvider>
      </NotificationsProvider>
      </MissionsProvider>
      </PlayerProvider>
    </ThemeProvider>
  );
}

const styles = StyleSheet.create({
  tabIconContainer: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    paddingVertical: 4,
  },
  badge: {
    position: 'absolute',
    right: -6,
    top: -3,
    borderRadius: 10,
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
});


